import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import klineApiService from '@/request/kline/kline.api.service';
import { IKline } from '@/request/kline/kline.response.interface';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IKlineReq } from '@/request/kline/kline.request.interface';
import { useTradeStore } from '../store';
import { useBoomWallet } from '@boomeme/wallet-sdk';

export const useRealPriceChg = () => {
  const { subscribe } = useAppEventBusContext();
  const { chain } = useBoomWallet();
  const { address } = useParams();
  const [klineData, setKlineData] = useState<IKline[]>([]);
  const [count, setCount] = useState(0);
  const { tokenInfo, setPriceChg } = useTradeStore();

  const fetchKlineData = async () => {
    try {
      if (address?.length && tokenInfo?.createdAt) {
        const now = new Date();
        const tokenCreateTime = new Date(tokenInfo?.createdAt * 1000);
        const isTokenOlderThan24Hours = now.getTime() - tokenCreateTime.getTime() >= 24 * 60 * 60 * 1000;
        const apiParams: IKlineReq = {
          tokenAddress: address,
          period: '1m',
        };
        if (isTokenOlderThan24Hours) {
          const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          const to = new Date(from.getTime() + 400 * 60 * 1000);
          apiParams.from = Math.floor(from.getTime() / 1000);
          apiParams.to = Math.floor(to.getTime() / 1000);
        } else {
          apiParams.from = tokenInfo?.createdAt;
          // const to = new Date(tokenInfo?.createdAt * 1000 + 400 * 60 * 1000);
          // apiParams.to = Math.floor(to.getTime() / 1000);
        }
        const res = await klineApiService.getKlineApi(apiParams);
        if (res.code == 0) {
          setKlineData(res.data);
          setCount(0);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchKlineData();
  }, [address, tokenInfo?.createdAt, chain]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCount((prev) => prev + 1);
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const calculatePriceChange = useCallback(
    (currentKline: IKline) => {
      if (!klineData.length) return;
      if (count >= klineData.length) {
        fetchKlineData();
        return;
      }

      if (tokenInfo?.createdAt) {
        const now = new Date();
        const tokenCreateTime = new Date(tokenInfo?.createdAt * 1000);
        const isTokenOlderThan24Hours = now.getTime() - tokenCreateTime.getTime() >= 24 * 60 * 60 * 1000;

        const oldPrice = isTokenOlderThan24Hours ? klineData[count].close : klineData[count].low;
        const currentPrice = currentKline.close;
        const priceChg = BigNumber(currentPrice).minus(oldPrice).dividedBy(oldPrice).toString();
        setPriceChg(priceChg);
      }
    },
    [klineData, count, tokenInfo?.createdAt],
  );
  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Realtime_Price, (newKline: IKline) => {
      if (!newKline) return;
      calculatePriceChange(newKline);
    });
    return () => unsubscribe();
  }, [calculatePriceChange]);

  return null;
};
